import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PwaService {
  private deferredPrompt: any = null;
  private showInstallButtonSubject = new BehaviorSubject<boolean>(false);

  public showInstallButton$: Observable<boolean> = this.showInstallButtonSubject.asObservable();

  constructor() {
    // Verificar si la app ya está instalada
    const isInstalled = this.isAppInstalled();

    if (isInstalled) {
      this.showInstallButtonSubject.next(false);
      console.log('PWA ya está instalada');
    } else {
      // Si no está instalada, mostrar el botón siempre
      this.showInstallButtonSubject.next(true);
    }

    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      // Asegurar que el botón se muestre si no está instalada
      if (!this.isAppInstalled()) {
        this.showInstallButtonSubject.next(true);
      }
    });

    window.addEventListener('appinstalled', (evt) => {
      this.showInstallButtonSubject.next(false);
      this.deferredPrompt = null;
    });
  }

  private isAppInstalled(): boolean {
    // Verificar si la app está corriendo en modo standalone
    return window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true;
  }

  private isChromeOrCompatible(): boolean {
    const ua = navigator.userAgent;

    // Navegadores incompatibles conocidos
    const incompatibleBrowsers = [
      'XiaoMi',
      'MiuiBrowser',
      'UCBrowser',
      'OPR', // Opera (no soporta bien PWA)
      'FB', // Facebook in-app browser
      'FBAN', // Facebook App
      'FBAV', // Facebook App
      'Instagram',
      'Line', // Line in-app browser
      'WeChat',
      'Twitter',
      'LinkedIn',
    ];

    // Verificar si es un navegador incompatible
    if (incompatibleBrowsers.some((browser) => ua.includes(browser))) {
      return false;
    }

    // Safari no es compatible con beforeinstallprompt pero se detecta aparte
    if (ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('CriOS')) {
      return false;
    }

    // Navegadores compatibles conocidos
    const compatibleBrowsers = [
      'Chrome',
      'CriOS', // Chrome en iOS
      'Edg', // Edge (Chromium)
      'EdgA', // Edge en Android
      'SamsungBrowser',
      'Firefox',
      'FxiOS', // Firefox en iOS
      'Brave', // Brave Browser
    ];

    return compatibleBrowsers.some((browser) => ua.includes(browser));
  }

  public openInChrome(): void {
    const currentUrl = window.location.href;
    // Intent para abrir en Chrome (Android)
    window.location.href = `googlechrome://navigate?url=${encodeURIComponent(currentUrl)}`;

    // Fallback: Si después de 2 segundos no se abrió Chrome, mostrar instrucciones
    setTimeout(() => {
      console.warn('Si Chrome no se abrió, por favor copia esta URL y ábrela manualmente en Chrome');
    }, 2000);
  }

  public isIncompatibleBrowser(): boolean {
    // Primero verificar si es un navegador conocido como incompatible
    if (!this.isChromeOrCompatible()) {
      return true;
    }
    // Si es compatible pero no hay prompt, también es problemático
    return !this.deferredPrompt;
  }

  public promptInstall(): void {
    // Si hay prompt disponible (navegador compatible), usarlo
    if (this.deferredPrompt) {
      this.deferredPrompt.prompt();

      this.deferredPrompt.userChoice.then((choiceResult: { outcome: string }) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('Usuario aceptó la instalación');
        } else {
          console.log('Usuario canceló la instalación');
        }
        this.deferredPrompt = null;
        this.showInstallButtonSubject.next(false);
      });
    } else {
      console.warn('Prompt no disponible. El componente debe manejar navegadores incompatibles.');
    }
  }
}
